import type { ReactNode } from 'react';
import React from 'react';
import type { SortEnd, SortEndHandler } from 'react-sortable-hoc';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import {
  RiCheckLine,
  RiDownload2Line,
  RiFolderZipLine,
  BulkAction,
  Button,
  Flex,
  Box,
  Grid,
  RiFilterOffLine,
  Modalv2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Dropdown,
  DropdownMenu
} from '@itandi/itandi-bb-ui';
import Styles from './ReportImagesArea.module.scss';
import type {
  DealReportBulkUpdateParams,
  DealReportImage,
  ReportImageBulkAction
} from '~/models/DealReportImage';
import {
  filterSelectedOwnImages,
  hasOperableSelectedImages,
  imagesExistInReport
} from '~/models/DealReportImage';
import { ReportImagesAreaBlock } from '~/components/common/reports/ReportImagesAreaBlock';
import type { NskResponse } from '../../../models/Api';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { renderDropDownButton } from '~/components/common/parts/dropdown/DropDownButton';

type StableSortableContainerProps = {
  items: DealReportImage[];
  renderImageBlock: (reportImage: DealReportImage, index: number) => ReactNode;
};

// SortableElementとSortableContainerをコンポーネント外で定義することで、再レンダー時の不要なアンマウントを防ぐ
// コンポーネント内で定義すると、毎回新しいコンポーネント型が生成されてしまう
const SortableItem = SortableElement<{ children: React.ReactNode }>(
  (props: { children: React.ReactNode }) => <>{props.children}</>
);
const SortableList = SortableContainer<StableSortableContainerProps>(
  (props: StableSortableContainerProps) => {
    const { items, renderImageBlock } = props;
    return (
      <div className={Styles.ReportImageList}>
        {items.map(renderImageBlock)}
      </div>
    );
  }
);

type Props = {
  reportImages: DealReportImage[];
  handleDownloadImage: (reportImage: DealReportImage) => void;
  handleUpdateSeq: (newOrderImages: DealReportImage[]) => Promise<void>;
  handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse<void>>;
  handleDestroy: (reportImage: DealReportImage) => Promise<NskResponse<void>>;
  handleDownloadPdf: () => void;
  handleDownloadZip: () => void;
  companyType: 'company' | 'vendor';
  onChangeSelectedImage: (reportImageId: number) => void;
  selectedImageIds: ReadonlyArray<number>;
  handleSelectedDownloadZip: () => void;
  handleBulkUpdate: (submitValue: DealReportBulkUpdateParams) => Promise<void>;
  handleBulkDelete: () => void;
  handleResetSelectedIds: () => void;
  handleSelectAllImages: () => void;
};

export const ReportImagesArea: React.FC<Props> = ({
  reportImages,
  handleDownloadImage,
  handleUpdateSeq,
  handleUpdate,
  handleDestroy,
  handleDownloadPdf,
  handleDownloadZip,
  companyType,
  onChangeSelectedImage,
  selectedImageIds,
  handleSelectedDownloadZip,
  handleBulkUpdate,
  handleBulkDelete,
  handleResetSelectedIds,
  handleSelectAllImages
}: Props) => {
  // 並び替えのためのstate
  const [orderImages, setOrderImages] = React.useState<DealReportImage[]>([]);
  const isVendor = companyType === 'vendor';
  const {
    isOpen: isOpenBulkDelete,
    handleOpen: handleOpenBulkDelete,
    handleClose: handleCloseBulkDelete
  } = useOpenHandler();
  React.useEffect(() => {
    setOrderImages(reportImages);
  }, [reportImages]);
  let tmpSeqNo = 0;
  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }: SortEnd) => {
    tmpSeqNo = 0;
    if (oldIndex === newIndex) {
      return;
    }
    const moveImage = orderImages[oldIndex];
    const newOrderItems: DealReportImage[] = [];
    const wkOrderImages = [...orderImages];
    wkOrderImages.splice(oldIndex, 1);
    wkOrderImages.splice(newIndex, 0, moveImage);
    wkOrderImages.forEach((r: DealReportImage, i: number) => {
      newOrderItems.push({ ...r, seq: i + 1 });
    });
    setOrderImages(newOrderItems);
    handleUpdateSeq(newOrderItems);
  };
  const renderImageBlock = (
    reportImage: DealReportImage,
    index: number
  ): ReactNode => {
    const displaySeqNo = ((): number | null => {
      if (isVendor) {
        return index + 1;
      }
      if (reportImage.outputPdfImage) {
        tmpSeqNo += 1;
        return tmpSeqNo;
      }
      return null;
    })();

    return (
      <SortableItem key={reportImage.id} index={index}>
        <ReportImagesAreaBlock
          key={reportImage.id}
          companyType={companyType}
          displaySeqNo={displaySeqNo}
          handleDestroy={handleDestroy}
          handleDownloadImage={handleDownloadImage}
          handleUpdate={handleUpdate}
          handleUpdateSeq={handleUpdateSeq}
          onChangeSelectedImage={onChangeSelectedImage}
          reportImage={reportImage}
          selectedImageIds={selectedImageIds}
        />
      </SortableItem>
    );
  };
  const showBulkAction = React.useMemo(
    (): boolean =>
      hasOperableSelectedImages(reportImages, selectedImageIds, companyType),
    [reportImages, selectedImageIds, companyType]
  );
  const bulkActionList = (): ReportImageBulkAction[] => {
    const actions: ReportImageBulkAction[] = [];
    actions.push({
      children: '画像をダウンロード',
      onClick: handleSelectedDownloadZip
    });
    if (showBulkAction) {
      actions.push({
        children: '施工推奨にする',
        onClick: () => handleBulkUpdate({ construction: 'targeted' })
      });
      actions.push({
        children: '施工推奨から外す',
        onClick: () => handleBulkUpdate({ construction: 'unspecified' })
      });
    }
    if (isVendor) {
      if (showBulkAction) {
        actions.push({
          children: '写真を共有する',
          onClick: () => handleBulkUpdate({ shared: true })
        });
        actions.push({
          children: '写真を共有しない',
          onClick: () => handleBulkUpdate({ shared: false })
        });
      }
    } else {
      actions.push({
        children: '写真を報告書に含める',
        onClick: () => handleBulkUpdate({ outputPdfImage: true })
      });
      actions.push({
        children: '写真を報告書に含めない',
        onClick: () => handleBulkUpdate({ outputPdfImage: false })
      });
      actions.push({
        children: '備考を報告書に含める',
        onClick: () => handleBulkUpdate({ outputPdfNote: true })
      });
      actions.push({
        children: '備考を報告書に含めない',
        onClick: () => handleBulkUpdate({ outputPdfNote: false })
      });
    }
    if (showBulkAction) {
      // 画像の削除は一番下に表示する
      actions.push({
        children: '画像を削除',
        danger: true,
        onClick: handleOpenBulkDelete
      });
    }
    return actions;
  };
  const canDownloadReport = imagesExistInReport(orderImages, isVendor);
  return (
    <div className={Styles.ReportImagesArea}>
      <Grid
        alignItems="center"
        autoFlow="column"
        gap={4}
        justifyContent="space-between"
        marginBottom={8}
      >
        <Box>
          <Flex gap={16} margin="8px 2px">
            <Button
              disabled={!canDownloadReport}
              endIcon={<RiDownload2Line />}
              onClick={handleDownloadPdf}
              variant="secondary"
            >
              報告書ダウンロード
            </Button>
            <Button
              disabled={orderImages.length <= 0}
              endIcon={<RiFolderZipLine />}
              onClick={handleDownloadZip}
              variant="secondary"
            >
              画像一括ダウンロード
            </Button>
          </Flex>
        </Box>
        <Box>
          <Flex gap={16} margin="8px 2px">
            <Button
              disabled={reportImages.length === 0}
              onClick={handleSelectAllImages}
              startIcon={<RiCheckLine />}
              variant="secondary"
            >
              全てを選択
            </Button>
            <Button
              disabled={selectedImageIds.length === 0}
              onClick={handleResetSelectedIds}
              startIcon={<RiFilterOffLine />}
              variant="secondary"
            >
              選択をクリア
            </Button>
          </Flex>
        </Box>
      </Grid>
      <BulkAction
        actionsArea={
          <Dropdown button={renderDropDownButton('まとめて操作')}>
            {bulkActionList().map((action) => (
              <DropdownMenu
                key={action.children}
                danger={action.danger}
                onClick={action.onClick}
              >
                {action.children}
              </DropdownMenu>
            ))}
          </Dropdown>
        }
        selectedCount={selectedImageIds.length}
      >
        <SortableList
          axis="xy"
          items={orderImages}
          onSortEnd={onSortEnd}
          onSortStart={(): void => {
            tmpSeqNo = 0;
          }}
          renderImageBlock={renderImageBlock}
          useDragHandle
        />
      </BulkAction>
      <Modalv2
        isOpen={isOpenBulkDelete}
        onClose={handleCloseBulkDelete}
        size="s"
      >
        <ModalHeader title="一括削除" />
        <ModalBody>
          {`${
            filterSelectedOwnImages(reportImages, selectedImageIds, companyType)
              .length
          }件の画像を削除してよろしいですか？`}
          <Alert mt={16} variant="warning">
            他社がアップロードした画像は削除されません。
          </Alert>
        </ModalBody>
        <ModalFooter
          leftArea={
            <Button
              onClick={handleCloseBulkDelete}
              type="button"
              variant="secondary"
            >
              キャンセル
            </Button>
          }
          rightArea={
            <Button
              onClick={(): void => {
                handleCloseBulkDelete();
                handleBulkDelete();
              }}
              type="button"
              variant="primary"
            >
              削除
            </Button>
          }
        />
      </Modalv2>
    </div>
  );
};
